import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { requestConversation } from "@/lib/conversation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function projectTitle(p) {
  return p.recommendation?.project_title || p.brief || "Untitled project";
}

export default function Projects() {
  const { user, loading: authLoading, signIn } = useAuth();
  const [projects, setProjects] = useState([]);
  const [blueprints, setBlueprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const [projRes, bpRes] = await Promise.all([
          axios.get(`${API}/me/projects`).catch(() => ({ data: { projects: [] } })),
          axios.get(`${API}/me/blueprints`).catch(() => ({ data: [] })),
        ]);
        setProjects(projRes.data?.projects || []);
        setBlueprints(Array.isArray(bpRes.data) ? bpRes.data : []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authLoading]);

  const empty = projects.length === 0 && blueprints.length === 0;

  return (
    <div className="min-h-screen bg-white text-ink flex flex-col">
      <Nav />

      <main className="flex-1">
        <section className="max-w-[1280px] mx-auto px-6 md:px-10 pt-24 md:pt-40 pb-16 md:pb-20">
          <div className="eyebrow eyebrow-denim mb-8">Your Projects</div>
          <h1 className="font-display font-extrabold text-[44px] sm:text-[64px] md:text-[88px] tracking-[-0.045em] leading-[0.96] text-ink max-w-[1100px]">
            Every idea,
            <br />
            <span className="font-serif-italic font-light">saved</span>.
          </h1>
        </section>

        <section className="max-w-[1280px] mx-auto px-6 md:px-10 pb-32 md:pb-48">
          {!user && !authLoading && (
            <div className="bnm-card p-8 md:p-12 max-w-[640px]" data-testid="projects-signin-card">
              <div className="eyebrow eyebrow-denim mb-3">Sign in</div>
              <h2 className="font-display font-semibold text-[24px] md:text-[30px] tracking-[-0.02em] leading-[1.2]">
                Come back to your projects, anytime.
              </h2>
              <p className="text-muted-ink text-[15px] mt-4 leading-[1.7] max-w-[440px]">
                Sign in to save every plan your Producer builds, pick them up tomorrow, next week, whenever you&rsquo;re ready.
              </p>
              <button
                type="button"
                onClick={signIn}
                className="btn-denim mt-7"
                data-testid="projects-signin"
              >
                <span>Sign in</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {user && loading && (
            <div className="text-muted-ink text-sm">Loading your projects…</div>
          )}

          {user && !loading && empty && (
            <div className="bnm-card p-8 md:p-12 max-w-[640px]" data-testid="projects-empty">
              <h2 className="font-display font-semibold text-[24px] md:text-[30px] tracking-[-0.02em] leading-[1.2]">
                No projects yet.
              </h2>
              <p className="text-muted-ink text-[15px] mt-4 leading-[1.7]">
                Share your first idea and your Producer will put together the plan.
              </p>
              <Link
                to="/"
                onClick={() => requestConversation()}
                className="btn-denim mt-7"
                data-testid="projects-start"
              >
                <span>Let{"\u2019"}s Begin</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {user && !loading && projects.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="projects-grid">
              {projects.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => navigate(`/projects/${p.id}`)}
                  className="bnm-card text-left p-6 md:p-7 hover:shadow-bnm transition-all group"
                  data-testid={`project-card-${p.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="eyebrow eyebrow-denim">
                        {new Date(p.created_at).toLocaleDateString(undefined, {
                          day: "numeric",
                          month: "short",
                        })}
                      </div>
                      {Number(p.version) > 1 && (
                        <span
                          className="text-[11px] tracking-[0.06em] uppercase text-muted-ink border border-bnm rounded-full px-2 py-0.5"
                          data-testid="project-version-badge"
                        >
                          v{p.version}
                        </span>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-ink group-hover:text-denim-bnm transition-colors" />
                  </div>
                  <h3 className="font-display font-extrabold text-[22px] md:text-[24px] tracking-[-0.025em] leading-[1.15] mt-5 text-ink">
                    {projectTitle(p)}
                  </h3>
                  {p.recommendation?.estimated_budget?.range && (
                    <p className="font-serif-italic text-[15px] text-muted-ink mt-3 leading-[1.4]">
                      {p.recommendation.estimated_budget.range}
                    </p>
                  )}
                  <div className="mt-6 pt-5 border-t border-bnm text-[13px] text-muted-ink truncate">
                    {p.brief}
                  </div>
                </button>
              ))}
            </div>
          )}

          {user && !loading && blueprints.length > 0 && (
            <div className={projects.length > 0 ? "mt-16 md:mt-24" : ""}>
              {projects.length > 0 && (
                <div className="eyebrow eyebrow-muted mb-6">Earlier blueprints</div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {blueprints.map((bp) => (
                  <button
                    key={bp.id}
                    type="button"
                    onClick={() => navigate(`/blueprint/${bp.id}`, { state: { blueprint: bp } })}
                    className="bnm-card text-left p-6 md:p-7 hover:shadow-bnm transition-all group"
                    data-testid={`project-card-${bp.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="eyebrow eyebrow-denim">
                        {new Date(bp.created_at).toLocaleDateString(undefined, {
                          day: "numeric",
                          month: "short",
                        })}
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-ink group-hover:text-denim-bnm transition-colors" />
                    </div>
                    <h3 className="font-display font-extrabold text-[22px] md:text-[24px] tracking-[-0.025em] leading-[1.15] mt-5 text-ink">
                      {bp.title}
                    </h3>
                    {bp.tagline && (
                      <p className="font-serif-italic text-[15px] text-muted-ink mt-3 leading-[1.4]">
                        &ldquo;{bp.tagline}&rdquo;
                      </p>
                    )}
                    <div className="mt-6 pt-5 border-t border-bnm text-[13px] text-muted-ink truncate">
                      {bp.idea}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}
